import {
    type AccountPaymentsSuccessResponse,
    type AccountRemovePaymentResponse,
    type AddPaymentErrorResponse,
    CardTokenizationError,
} from '@libs/common-next';
import { NotFoundError } from '@libs/core';

import { cweaClient } from '../clients/api-proxy-browser-client';
import { CWEA_ENDPOINTS } from '../constants';
import { type ApiResponse, unwrapApiResponseOrThrow } from './utils';

const CARD_TOKENIZATION_REJECTED_MESSAGE = 'Credit Card tokenization is rejected by psp';
const CARD_REJECTED_MESSAGE = 'Could not add card';
const PAYMENT_PROCESSOR_ERROR_MESSAGE = '[BFF] Error reaching payment processor';

const PAYMENT_KNOWN_ERROR_DETAILS = [
    CARD_TOKENIZATION_REJECTED_MESSAGE,
    CARD_REJECTED_MESSAGE,
    PAYMENT_PROCESSOR_ERROR_MESSAGE,
];

const throwIfKnownPaymentError = (response: unknown) => {
    if (response && typeof response === 'object' && 'detail' in response) {
        const detail = (response as { detail: string }).detail;

        const hasKnownError = PAYMENT_KNOWN_ERROR_DETAILS.some(error => detail.includes(error));

        if (hasKnownError) throw new CardTokenizationError((response as { detail: string }).detail);
    }
};

export async function addPayment(body: Record<string, unknown>) {
    const response = await cweaClient.post<
        ApiResponse<AccountPaymentsSuccessResponse | AddPaymentErrorResponse>,
        Record<string, unknown>
    >(CWEA_ENDPOINTS.payments.addPayment, {
        body,
        additionalSuccessCodes: [400],
    });

    throwIfKnownPaymentError(response);

    return unwrapApiResponseOrThrow(response, 'Failed to add payment method');
}

export async function updatePayment(cardId: number, body: Record<string, unknown>) {
    const response = await cweaClient.put<
        ApiResponse<AccountPaymentsSuccessResponse | AddPaymentErrorResponse>,
        Record<string, unknown>
    >(CWEA_ENDPOINTS.payments.updatePayment(cardId), {
        body,
        additionalSuccessCodes: [400],
    });

    throwIfKnownPaymentError(response);

    return unwrapApiResponseOrThrow(response, 'Failed to update payment method');
}

export async function removePayment(cardId: number) {
    const response = await cweaClient.delete<AccountRemovePaymentResponse>(
        CWEA_ENDPOINTS.payments.removePayment(cardId),
        {
            additionalSuccessCodes: [404],
        }
    );

    if (response == null) return null;

    if ('status' in response && response.status === 404) {
        throw new NotFoundError();
    }

    return unwrapApiResponseOrThrow(response, 'Failed to remove payment method');
}
